import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  FileText, 
  Shield, 
  Database, 
  AlertTriangle, 
  Mail, 
  Lock, 
  Eye, 
  Users, 
  Scale,
  CheckCircle,
  Globe,
  Server
} from 'lucide-react';

const TermsOfUse = () => {
  const lastUpdated = "July 30, 2026";
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4" data-testid="terms-of-use-page">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
        
        {/* Header */}
        <Card className="mb-8">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold">Terms of Service & Privacy Policy</CardTitle>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Last Updated: {lastUpdated}
            </p>
          </CardHeader>
        </Card>

        {/* Table of Contents */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <a href="#acceptance" className="text-emerald-600 hover:underline">1. Acceptance of Terms</a>
              <a href="#service" className="text-emerald-600 hover:underline">2. Service Description</a>
              <a href="#data-ownership" className="text-emerald-600 hover:underline">3. Data Ownership & Rights</a>
              <a href="#privacy" className="text-emerald-600 hover:underline">4. Privacy Policy</a>
              <a href="#data-security" className="text-emerald-600 hover:underline">5. Data Security & Certifications</a>
              <a href="#state-compliance" className="text-emerald-600 hover:underline">6. State Regulatory Compliance</a>
              <a href="#information-sharing" className="text-emerald-600 hover:underline">7. Information Sharing & Disclosure</a>
              <a href="#consent" className="text-emerald-600 hover:underline">8. Consent & Data Processing</a>
              <a href="#subscription" className="text-emerald-600 hover:underline">9. Subscription & Payment</a>
              <a href="#user-responsibilities" className="text-emerald-600 hover:underline">10. User Responsibilities</a>
              <a href="#liability" className="text-emerald-600 hover:underline">11. Limitation of Liability</a>
              <a href="#termination" className="text-emerald-600 hover:underline">12. Termination</a>
              <a href="#governing-law" className="text-emerald-600 hover:underline">13. Governing Law</a>
              <a href="#contact" className="text-emerald-600 hover:underline">14. Contact Information</a>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="space-y-6">
          {/* Section 1: Acceptance */}
          <Card id="acceptance">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                1. Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                Welcome to the Pure Solar Systems CRM Platform (&ldquo;Service&rdquo;), operated by Pure Solar Systems LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). 
                By accessing or using our Service, you agree to be bound by these Terms of Service and Privacy Policy (&ldquo;Terms&rdquo;). If you do not agree to these Terms, 
                you may not access or use the Service.
              </p>
              <p>
                These Terms apply to all users, including organizations (&ldquo;Tenants&rdquo;), their employees, representatives, and any individual who accesses customer data through the Service.
              </p>
              <p>
                <strong>BY USING THIS SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS, INCLUDING OUR DATA COLLECTION, USE, AND SHARING PRACTICES DESCRIBED HEREIN.</strong>
              </p>
            </CardContent>
          </Card>

          {/* Section 2: Service Description */}
          <Card id="service">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                2. Service Description
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                Pure Solar Systems CRM is a software-as-a-service (SaaS) platform designed for solar sales organizations to manage:
              </p>
              <ul>
                <li>Customer leads and contact information</li>
                <li>Sales pipeline and deal tracking</li>
                <li>Commission calculations and payout management</li>
                <li>Team hierarchy and performance reporting</li>
                <li>Business analytics and reporting</li>
              </ul>
              <p>
                The Service processes personal information of your customers (&ldquo;Customer Data&rdquo;) on your behalf. You are the &ldquo;Data Controller&rdquo; and we act as your &ldquo;Data Processor&rdquo; as defined by applicable privacy laws.
              </p>
            </CardContent>
          </Card>

          {/* Section 3: Data Ownership */}
          <Card id="data-ownership" className="border-emerald-200 dark:border-emerald-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                <Database className="w-5 h-5" />
                3. Data Ownership & Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg mb-4 border border-emerald-200 dark:border-emerald-800">
                <p className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                  Important: Your Data Belongs to You
                </p>
                <p className="text-emerald-700 dark:text-emerald-300">
                  Any and all data you input, upload, or generate through the Service (&ldquo;Your Data&rdquo;), including Customer Data, remains your exclusive property. 
                  Pure Solar Systems LLC does not claim any ownership rights over Your Data.
                </p>
              </div>
              <ul>
                <li><strong>Your Rights:</strong> You retain all rights, title, and interest in Your Data, including all intellectual property rights.</li>
                <li><strong>Our Limited Use:</strong> We only access Your Data to: (a) provide the Service, (b) troubleshoot issues at your request, (c) improve our Service in aggregate anonymized form, or (d) as required by law.</li>
                <li><strong>Data Portability:</strong> You may export Your Data at any time through the Service&apos;s export features in standard formats (CSV, PDF).</li>
                <li><strong>Data Deletion:</strong> Upon termination of your subscription, you may request complete deletion of Your Data within 30 days. We will certify deletion upon request.</li>
                <li><strong>No Sale of Data:</strong> We do not sell, rent, lease, or share Your Data or Customer Data with third parties for their marketing or commercial purposes.</li>
                <li><strong>No Data Mining:</strong> We do not use Your Data to build profiles, train AI models, or for any purpose beyond providing the Service.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 4: Privacy Policy */}
          <Card id="privacy" className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Eye className="w-5 h-5" />
                4. Privacy Policy
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4 className="font-semibold">4.1 Information We Collect</h4>
              <p><strong>Account Information:</strong></p>
              <ul>
                <li>Name, email address, phone number</li>
                <li>Company/organization name and address</li>
                <li>Billing and payment information (processed by Stripe)</li>
                <li>Account credentials (passwords are encrypted and never stored in plain text)</li>
              </ul>
              
              <p><strong>Customer Data You Provide:</strong></p>
              <ul>
                <li>Customer names, addresses, contact information</li>
                <li>Property and installation details</li>
                <li>Sales and commission records</li>
                <li>Notes and communication logs</li>
              </ul>
              
              <p><strong>Automatically Collected Information:</strong></p>
              <ul>
                <li>IP addresses and device information</li>
                <li>Browser type and operating system</li>
                <li>Usage data and access logs</li>
                <li>Cookies for session management (no third-party tracking cookies)</li>
              </ul>

              <h4 className="font-semibold mt-6">4.2 How We Use Your Information</h4>
              <ul>
                <li>To provide, maintain, and improve the Service</li>
                <li>To process transactions and send billing notifications</li>
                <li>To respond to your requests and provide customer support</li>
                <li>To send important Service updates and security alerts</li>
                <li>To detect, prevent, and address fraud or security issues</li>
                <li>To comply with legal obligations</li>
              </ul>

              <h4 className="font-semibold mt-6">4.3 Legal Basis for Processing</h4>
              <ul>
                <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you requested</li>
                <li><strong>Legitimate Interests:</strong> Improving our Service, preventing fraud, ensuring security</li>
                <li><strong>Legal Compliance:</strong> Processing required by applicable laws</li>
                <li><strong>Consent:</strong> Where you have explicitly consented to specific processing</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 5: Data Security */}
          <Card id="data-security" className="border-purple-200 dark:border-purple-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                <Lock className="w-5 h-5" />
                5. Data Security & Certifications
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg mb-4 border border-purple-200 dark:border-purple-800">
                <p className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
                  Our Commitment to Security
                </p>
                <p className="text-purple-700 dark:text-purple-300">
                  We implement industry-standard security measures to protect your data and comply with applicable data protection regulations.
                </p>
              </div>

              <h4 className="font-semibold">5.1 Security Measures</h4>
              <ul>
                <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS 1.3 (HTTPS)</li>
                <li><strong>Encryption at Rest:</strong> All stored data is encrypted using AES-256 encryption</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt with salt; we never store plain-text passwords</li>
                <li><strong>Access Controls:</strong> Role-based access control (RBAC) ensures users only access data they&apos;re authorized to view</li>
                <li><strong>Multi-Tenant Isolation:</strong> Each organization&apos;s data is logically isolated; strict query filtering prevents cross-tenant data access</li>
                <li><strong>Secure Infrastructure:</strong> Our servers are hosted in SOC 2 compliant data centers with 24/7 monitoring</li>
                <li><strong>Regular Backups:</strong> Automated daily backups with point-in-time recovery capability</li>
                <li><strong>Audit Logging:</strong> All data access and modifications are logged for audit purposes</li>
              </ul>

              <h4 className="font-semibold mt-6">5.2 Security Certifications & Compliance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                  <p className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    SOC 2 Type II Compliant Infrastructure
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Data centers meet AICPA security standards</p>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                  <p className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    PCI DSS Compliant Payments
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Payment processing via Stripe (PCI Level 1)</p>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                  <p className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    HTTPS/TLS 1.3 Encryption
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Bank-grade encryption for all transmissions</p>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                  <p className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    GDPR & CCPA Ready
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Privacy controls for global compliance</p>
                </div>
              </div>

              <h4 className="font-semibold mt-6">5.3 Incident Response</h4>
              <p>
                In the event of a data breach affecting your Customer Data, we will:
              </p>
              <ul>
                <li>Notify you within 72 hours of discovering the breach</li>
                <li>Provide details of the nature and scope of the breach</li>
                <li>Describe measures taken to address and mitigate the breach</li>
                <li>Cooperate with your notification obligations to affected individuals and regulators</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 6: State Regulatory Compliance */}
          <Card id="state-compliance" className="border-amber-200 dark:border-amber-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Scale className="w-5 h-5" />
                6. State Regulatory Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                We comply with state-specific data protection and privacy regulations. The following rights are available based on your jurisdiction:
              </p>

              <h4 className="font-semibold mt-4">6.1 California Consumer Privacy Act (CCPA) / California Privacy Rights Act (CPRA)</h4>
              <p>If you are a California resident, you have the right to:</p>
              <ul>
                <li><strong>Right to Know:</strong> Request disclosure of personal information we collect, use, disclose, and sell</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information (subject to certain exceptions)</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (Note: We do NOT sell personal information)</li>
                <li><strong>Right to Non-Discrimination:</strong> Not receive discriminatory treatment for exercising your privacy rights</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Limit Use:</strong> Limit the use and disclosure of sensitive personal information</li>
              </ul>
              <p className="text-sm bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                <strong>Notice:</strong> We do NOT sell or share personal information for cross-context behavioral advertising. 
                We do NOT use or disclose sensitive personal information for purposes other than providing the Service.
              </p>

              <h4 className="font-semibold mt-6">6.2 Virginia Consumer Data Protection Act (VCDPA)</h4>
              <p>If you are a Virginia resident, you have the right to:</p>
              <ul>
                <li>Confirm whether we are processing your personal data</li>
                <li>Access your personal data</li>
                <li>Correct inaccuracies in your personal data</li>
                <li>Delete your personal data</li>
                <li>Obtain a copy of your personal data in a portable format</li>
                <li>Opt out of targeted advertising and sale of personal data</li>
              </ul>

              <h4 className="font-semibold mt-6">6.3 Colorado Privacy Act (CPA)</h4>
              <p>If you are a Colorado resident, you have similar rights including:</p>
              <ul>
                <li>Right to access, correct, delete, and obtain a copy of your data</li>
                <li>Right to opt out of targeted advertising, sale of data, and profiling</li>
              </ul>

              <h4 className="font-semibold mt-6">6.4 Other State Laws</h4>
              <p>
                We also comply with data protection requirements under Connecticut, Utah, Texas, Oregon, Montana, and other state privacy laws as applicable. 
                If your state provides additional rights, please contact us to exercise them.
              </p>

              <h4 className="font-semibold mt-6">6.5 Exercising Your Rights</h4>
              <p>To exercise any of the above rights, please contact us at:</p>
              <ul>
                <li>Email: <a href="mailto:privacy@puresolarsystems.com" className="text-emerald-600">privacy@puresolarsystems.com</a></li>
                <li>We will respond to verifiable requests within 45 days (or as required by applicable law)</li>
                <li>You may designate an authorized agent to make requests on your behalf</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 7: Information Sharing */}
          <Card id="information-sharing" className="border-red-200 dark:border-red-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <Users className="w-5 h-5" />
                7. Information Sharing & Disclosure
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-4 border border-red-200 dark:border-red-800">
                <p className="font-semibold text-red-800 dark:text-red-200 mb-2">
                  We Do NOT Sell Your Data
                </p>
                <p className="text-red-700 dark:text-red-300">
                  Pure Solar Systems LLC does not sell, rent, or lease personal information or Customer Data to third parties under any circumstances.
                </p>
              </div>

              <h4 className="font-semibold">7.1 When We May Share Information</h4>
              <p>We may share your information only in the following limited circumstances:</p>
              
              <p><strong>Service Providers (Subprocessors):</strong></p>
              <ul>
                <li><strong>Stripe, Inc.</strong> - Payment processing (PCI DSS Level 1 certified)</li>
                <li><strong>MongoDB Atlas</strong> - Database hosting (SOC 2 Type II certified)</li>
                <li><strong>Cloud Infrastructure Provider</strong> - Server hosting (SOC 2 compliant)</li>
              </ul>
              <p className="text-sm">
                All subprocessors are contractually bound to maintain confidentiality and implement appropriate security measures.
              </p>

              <p><strong>Legal Requirements:</strong></p>
              <ul>
                <li>When required by law, subpoena, court order, or government request</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>To enforce our Terms of Service</li>
                <li>In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
              </ul>

              <p><strong>With Your Consent:</strong></p>
              <ul>
                <li>When you explicitly authorize us to share specific information</li>
                <li>When you use integrations that require data sharing (e.g., Gmail integration)</li>
              </ul>

              <h4 className="font-semibold mt-6">7.2 Information We Never Share</h4>
              <ul>
                <li>We never share data with third parties for their marketing purposes</li>
                <li>We never sell or license access to your Customer Data</li>
                <li>We never use Customer Data for advertising or profiling</li>
                <li>We never share data with data brokers</li>
              </ul>

              <h4 className="font-semibold mt-6">7.3 International Data Transfers</h4>
              <p>
                Your data is stored and processed in the United States. If you are accessing the Service from outside the United States, 
                you consent to the transfer, storage, and processing of your information in the United States, where data protection laws may differ from those in your country.
              </p>
            </CardContent>
          </Card>

          {/* Section 8: Consent */}
          <Card id="consent" className="border-indigo-200 dark:border-indigo-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
                <CheckCircle className="w-5 h-5" />
                8. Consent & Data Processing
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4 className="font-semibold">8.1 Your Consent</h4>
              <p>By using the Service, you consent to:</p>
              <ul>
                <li>The collection and processing of your account information as described in this policy</li>
                <li>The storage and processing of Customer Data you input into the Service</li>
                <li>Receiving transactional emails related to the Service (billing, security alerts, etc.)</li>
                <li>The use of cookies for session management and Service functionality</li>
              </ul>

              <h4 className="font-semibold mt-6">8.2 Your Responsibilities as Data Controller</h4>
              <p>As a user of the Service who inputs Customer Data, you acknowledge that:</p>
              <ul>
                <li>You are the Data Controller for Customer Data you input</li>
                <li>You have obtained all necessary consents from your customers to collect and process their data</li>
                <li>You will use Customer Data only for legitimate business purposes</li>
                <li>You will comply with all applicable privacy laws regarding your customers&apos; data</li>
                <li>You will notify affected individuals if their data is compromised</li>
              </ul>

              <h4 className="font-semibold mt-6">8.3 Withdrawing Consent</h4>
              <p>
                You may withdraw consent at any time by closing your account. However, withdrawal of consent does not affect 
                the lawfulness of processing based on consent before its withdrawal, nor processing based on other legal grounds.
              </p>

              <h4 className="font-semibold mt-6">8.4 Data Retention</h4>
              <ul>
                <li><strong>Active Accounts:</strong> Data is retained for the duration of your subscription</li>
                <li><strong>After Termination:</strong> Data is retained for 30 days to allow for export, then permanently deleted</li>
                <li><strong>Backup Retention:</strong> Backups may retain data for up to 90 days for disaster recovery purposes</li>
                <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law (e.g., financial records)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 9: Subscription & Payment */}
          <Card id="subscription">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                9. Subscription & Payment Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <ul>
                <li><strong>Free Trial:</strong> New subscribers receive a 14-day free trial. A valid credit card is required to start the trial, but you will not be charged until the trial period ends.</li>
                <li><strong>Subscription Fee:</strong> After the trial, the subscription fee of $349.99 per month will be automatically charged to your payment method.</li>
                <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly in advance.</li>
                <li><strong>No Refunds:</strong> All subscription fees are non-refundable. You may cancel at any time, but no refunds will be issued for partial months or unused time.</li>
                <li><strong>Failed Payments:</strong> If payment fails, your access may be suspended until payment is successfully processed.</li>
                <li><strong>Price Changes:</strong> We reserve the right to change pricing with 30 days&apos; notice to active subscribers.</li>
                <li><strong>Payment Security:</strong> All payment processing is handled by Stripe, which is PCI DSS Level 1 certified. We never store your full credit card number.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 10: User Responsibilities */}
          <Card id="user-responsibilities">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                10. User Responsibilities
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>By using the Service, you agree to:</p>
              <ul>
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security and confidentiality of your account credentials</li>
                <li>Not share your account with unauthorized users</li>
                <li>Immediately notify us of any unauthorized access to your account</li>
                <li>Comply with all applicable federal, state, and local laws in your use of the Service</li>
                <li>Obtain proper consent before entering Customer Data into the Service</li>
                <li>Not use the Service for any illegal, fraudulent, or harmful purposes</li>
                <li>Not attempt to access other users&apos; or organizations&apos; data without authorization</li>
                <li>Not interfere with or disrupt the Service or its infrastructure</li>
                <li>Not reverse engineer, decompile, or attempt to extract source code from the Service</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 11: Limitation of Liability */}
          <Card id="liability" className="border-amber-200 dark:border-amber-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-5 h-5" />
                11. Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <ul>
                <li>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</li>
                <li>WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, COMPLETELY SECURE, OR FREE FROM VIRUSES OR OTHER HARMFUL COMPONENTS.</li>
                <li>IN NO EVENT SHALL PURE SOLAR SYSTEMS LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOST DATA, OR BUSINESS INTERRUPTION.</li>
                <li>OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).</li>
              </ul>
              <p>
                You acknowledge that you use the Service at your own risk and that you are solely responsible for your business decisions 
                based on information in the Service. Some jurisdictions do not allow limitations on implied warranties or liability, so the above limitations may not apply to you.
              </p>
            </CardContent>
          </Card>

          {/* Section 12: Termination */}
          <Card id="termination">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" />
                12. Termination
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <ul>
                <li><strong>By You:</strong> You may cancel your subscription at any time through your account settings or by contacting us. Cancellation takes effect at the end of the current billing period.</li>
                <li><strong>By Us:</strong> We may suspend or terminate your access immediately if you: (a) violate these Terms, (b) fail to pay, (c) engage in fraudulent or illegal activity, or (d) engage in conduct that harms the Service or other users.</li>
                <li><strong>Effect of Termination:</strong> Upon termination:
                  <ul>
                    <li>Your access to the Service will cease immediately or at end of billing period</li>
                    <li>You have 30 days to export Your Data</li>
                    <li>After 30 days, Your Data will be permanently deleted</li>
                    <li>You remain liable for any fees incurred before termination</li>
                  </ul>
                </li>
                <li><strong>Survival:</strong> Sections relating to Data Ownership, Limitation of Liability, and Governing Law survive termination.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 13: Governing Law */}
          <Card id="governing-law">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-slate-600" />
                13. Governing Law & Disputes
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the District of Columbia, United States, 
                without regard to its conflict of law principles.
              </p>
              <p>
                Any disputes arising from these Terms or the Service shall be resolved as follows:
              </p>
              <ul>
                <li><strong>Informal Resolution:</strong> We encourage you to contact us first to resolve disputes informally.</li>
                <li><strong>Binding Arbitration:</strong> If informal resolution fails, disputes shall be resolved through binding arbitration 
                in Washington, D.C., administered by the American Arbitration Association under its Commercial Arbitration Rules.</li>
                <li><strong>Class Action Waiver:</strong> You agree to resolve disputes on an individual basis only. You waive your right to participate in class actions.</li>
                <li><strong>Small Claims Exception:</strong> Either party may bring claims in small claims court if eligible.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 14: Contact */}
          <Card id="contact">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                14. Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>For questions about these Terms, Privacy Policy, or to exercise your data rights, please contact us:</p>
              <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-lg">
                <p className="font-semibold">Pure Solar Systems LLC</p>
                <p>3829 Pennsylvania Ave SE</p>
                <p>Washington DC 20020</p>
                <p className="mt-4">
                  <strong>General Inquiries:</strong><br />
                  <a href="mailto:info@puresolarsystems.com" className="text-emerald-600 hover:underline">
                    info@puresolarsystems.com
                  </a>
                </p>
                <p className="mt-2">
                  <strong>Privacy & Data Requests:</strong><br />
                  <a href="mailto:privacy@puresolarsystems.com" className="text-emerald-600 hover:underline">
                    privacy@puresolarsystems.com
                  </a>
                </p>
                <p className="mt-2">
                  <strong>Security Concerns:</strong><br />
                  <a href="mailto:security@puresolarsystems.com" className="text-emerald-600 hover:underline">
                    security@puresolarsystems.com
                  </a>
                </p>
              </div>
              <p className="mt-4 text-sm">
                We will respond to all inquiries within 5 business days. Privacy-related requests will be processed within the timeframes required by applicable law.
              </p>
            </CardContent>
          </Card>

          {/* Acceptance */}
          <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-6 text-center">
              <p className="text-emerald-800 dark:text-emerald-200 font-medium">
                By creating an account or using the Service, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service and Privacy Policy, including our data collection, 
                use, and sharing practices described herein.
              </p>
            </CardContent>
          </Card>

          {/* Updates Notice */}
          <Card>
            <CardContent className="p-6">
              <h4 className="font-semibold mb-2">Updates to These Terms</h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                We may update these Terms from time to time. If we make material changes, we will notify you by email 
                or through the Service at least 30 days before the changes take effect. Your continued use of the Service 
                after the changes take effect constitutes acceptance of the updated Terms.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Pure Solar Systems LLC. All rights reserved.</p>
          <p className="mt-1">Document Version: 2.0 | Effective Date: July 30, 2026</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
